import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { getRoles, getCategories, getJDPrepSessions } from '../../services/userApi';
import PublicNavbar from '../../components/common/PublicNavbar';
import { useAuth } from '../../context/AuthContext';
import PageMeta from '../../components/common/PageMeta';

// ── SVG Icons ──────────────────────────────────────────────────────────────────
function IconSearch({ className = 'w-5 h-5' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}
function IconChevronRight({ className = 'w-4 h-4' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}
function IconX({ className = 'w-4 h-4' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}
function IconSparkle({ className = 'w-4 h-4' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}
function IconArrow({ className = 'w-4 h-4' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
    </svg>
  );
}
function IconBook({ className = 'w-3 h-3' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 19.5A2.5 2.5 0 016.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z" />
    </svg>
  );
}
function IconShare({ className = 'w-3.5 h-3.5' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
    </svg>
  );
}

async function shareRole(e, title, slug) {
  e.preventDefault();
  e.stopPropagation();
  const url = `https://centricq.com/roles/${slug}`;
  if (navigator.share) {
    try { await navigator.share({ title: `${title} Interview Questions — CentricQ`, url }); } catch {}
  } else {
    try { await navigator.clipboard.writeText(url); toast.success('Link copied!'); } catch { toast.error('Could not copy link'); }
  }
}

// Category colour palette — cycles through a fixed set so each category gets a consistent pill colour
const CATEGORY_COLORS = [
  { bg: '#EDE9FE', color: '#7C3AED' },
  { bg: '#FEF3C7', color: '#D97706' },
  { bg: '#D1FAE5', color: '#059669' },
  { bg: '#DBEAFE', color: '#1D4ED8' },
  { bg: '#FCE7F3', color: '#BE185D' },
  { bg: '#FEE2E2', color: '#DC2626' },
  { bg: '#E0F2FE', color: '#0369A1' },
];

const categoryColorCache = {};
let colorIndex = 0;

function getCategoryColor(category) {
  if (!categoryColorCache[category]) {
    categoryColorCache[category] = CATEGORY_COLORS[colorIndex % CATEGORY_COLORS.length];
    colorIndex++;
  }
  return categoryColorCache[category];
}

function Nav() { return <PublicNavbar forceLight />; }

// ── Role Card ─────────────────────────────────────────────────────────────────
function RoleCard({ role }) {
  const [hovered, setHovered] = useState(false);
  const navigate = useNavigate();
  const catColor = getCategoryColor(role.category);

  return (
    <Link to={`/roles/${role.slug}`} className="no-underline block group">
      <div
        className="bg-white border rounded-[20px] p-5 h-full flex flex-col transition-all duration-200 cursor-pointer relative overflow-hidden"
        style={{
          borderColor: hovered ? '#1B3A5C' : '#E2E8F0',
          transform: hovered ? 'translateY(-3px)' : 'none',
          boxShadow: hovered ? '0 4px 24px rgba(0,0,0,0.10), 0 1px 3px rgba(0,0,0,0.06)' : '0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.06)',
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {/* Title row */}
        <div className="flex items-start justify-between gap-2 mb-1.5">
          <p className="font-bold leading-[1.35] transition-colors duration-150 min-w-0" style={{ fontSize: 15, color: hovered ? '#1B3A5C' : '#0F172A' }}>
            {role.title}
          </p>
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 mt-0.5" style={{ background: '#D1FAE5', color: '#059669', whiteSpace: 'nowrap' }}>
            100 free (Ch.1)
          </span>
        </div>
        {role.description && (
          <p className="leading-[1.55] mb-2 line-clamp-2" style={{ fontSize: 12, color: '#64748B' }}>
            {role.description}
          </p>
        )}

        {/* Practice formats + course link */}
        <div className="flex items-center justify-between gap-1 mb-3 mt-1">
          <div className="flex items-center gap-1">
            {[
              { label: 'MCQ',     bg: '#EFF6FF', color: '#2563EB' },
              { label: 'Written', bg: '#EDE9FE', color: '#7C3AED' },
              { label: 'Spoken',  bg: '#FEF3C7', color: '#D97706' },
            ].map(({ label, bg, color }) => (
              <span key={label} className="font-bold" style={{ fontSize: 10, padding: '2px 7px', borderRadius: 6, background: bg, color }}>
                {label}
              </span>
            ))}
          </div>
          <button
            onClick={e => { e.preventDefault(); e.stopPropagation(); navigate('/courses'); }}
            title="Browse study guides"
            className="flex items-center gap-1 font-semibold transition-all duration-150 cursor-pointer border-0 flex-shrink-0"
            style={{ fontSize: 11, color: '#94A3B8', background: 'transparent', padding: '2px 6px', borderRadius: 6, fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" }}
            onMouseEnter={e => { e.currentTarget.style.background = '#EFF6FF'; e.currentTarget.style.color = '#1B3A5C'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#94A3B8'; }}
          >
            <IconBook className="w-3 h-3" /> Study Guide
          </button>
        </div>

        {/* Bottom row */}
        <div className="mt-auto flex items-center justify-between gap-2 flex-wrap pt-3" style={{ borderTop: '1px solid #F1F5F9' }}>
          <div className="flex gap-1.5 flex-wrap">
            <span className="text-[11px] px-2 py-1 rounded-lg font-medium" style={{ background: '#F0F6FF', color: '#64748B' }}>1,000 Qs</span>
            <span className="text-[11px] px-2 py-1 rounded-lg font-bold" style={{ background: catColor.bg, color: catColor.color }}>{role.category}</span>
            {!role.is_active && (
              <span className="text-[11px] px-2 py-1 rounded-lg font-semibold" style={{ background: '#FEF3C7', color: '#D97706' }}>Soon</span>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={e => shareRole(e, role.title, role.slug)}
              title="Share this role"
              className="flex items-center justify-center transition-all duration-150 cursor-pointer border-0"
              style={{ width: 26, height: 26, borderRadius: 6, background: hovered ? '#F0F6FF' : 'transparent', color: '#94A3B8', padding: 0 }}
              onMouseEnter={e => { e.currentTarget.style.background = '#E0EDFF'; e.currentTarget.style.color = '#1B3A5C'; }}
              onMouseLeave={e => { e.currentTarget.style.background = hovered ? '#F0F6FF' : 'transparent'; e.currentTarget.style.color = '#94A3B8'; }}
            >
              <IconShare className="w-3.5 h-3.5" />
            </button>
            <span className="text-[13px] font-bold transition-all duration-200 flex items-center gap-0.5"
              style={{ color: hovered ? '#1B3A5C' : '#94A3B8', opacity: hovered ? 1 : 0.6 }}>
              View <IconChevronRight className="w-3.5 h-3.5" />
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}

// ── Skeleton ──────────────────────────────────────────────────────────────────
function SkeletonCard() {
  return (
    <div className="bg-white border rounded-[20px] p-5 animate-pulse" style={{ borderColor: '#E2E8F0' }}>
      <div className="h-4 rounded-lg w-3/4 mb-2" style={{ background: '#F0F6FF' }} />
      <div className="h-3 rounded-lg w-full mb-1" style={{ background: '#F0F6FF' }} />
      <div className="h-3 rounded-lg w-2/3 mb-4" style={{ background: '#F0F6FF' }} />
      <div className="flex gap-2">
        <div className="h-5 rounded-lg w-14" style={{ background: '#F0F6FF' }} />
        <div className="h-5 rounded-lg w-16" style={{ background: '#F0F6FF' }} />
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function BrowseRolesPage() {
  const { isAuthenticated } = useAuth();
  const [roles,          setRoles]          = useState([]);
  const [categories,     setCategories]     = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [loadError,      setLoadError]      = useState(false);
  const [search,         setSearch]         = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [sidebarOpen,    setSidebarOpen]    = useState(false);
  const [jdSessions,     setJdSessions]     = useState([]);

  const loadRoles = () => {
    setLoading(true);
    setLoadError(false);
    Promise.all([
      getRoles().then(r => setRoles(r.data)),
      getCategories().then(r => setCategories([{ category: 'All', role_count: 0 }, ...r.data])),
    ])
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadRoles(); }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    getJDPrepSessions()
      .then(r => setJdSessions(r.data.filter(s => s.status === 'completed').slice(0, 3)))
      .catch(() => {});
  }, [isAuthenticated]);

  const filtered = roles.filter(r => {
    const matchSearch = r.title.toLowerCase().includes(search.toLowerCase()) ||
                        r.category.toLowerCase().includes(search.toLowerCase());
    const matchCat = activeCategory === 'All' || r.category === activeCategory;
    return matchSearch && matchCat;
  });

  const grouped = filtered.reduce((acc, role) => {
    if (!acc[role.category]) acc[role.category] = [];
    acc[role.category].push(role);
    return acc;
  }, {});

  const itemListJsonLd = roles.length > 0 ? JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Interview Preparation Roles',
    description: 'Browse 150+ job roles for interview preparation on CentricQ.',
    url: 'https://centricq.com/roles',
    numberOfItems: roles.length,
    itemListElement: roles.slice(0, 50).map((r, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: `${r.title} Interview Questions`,
      url: `https://centricq.com/roles/${r.slug}`,
    })),
  }) : null;

  return (
    <div style={{ minHeight: '100vh', background: '#FFFFFF', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif", color: '#0F172A' }}>
      <PageMeta
        title="Browse 150+ Interview Roles"
        description="Browse 150+ job roles. Practice 1,000 questions per role with AI feedback. MCQ, written, and spoken formats. 100 questions free (Chapter 1) — no credit card needed."
        path="/roles"
      />
      {itemListJsonLd && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: itemListJsonLd }} />}
      <style>{`
        *, *::before, *::after { box-sizing: border-box; }
        @media (prefers-reduced-motion: reduce) {
          *, *::before, *::after { transition-duration: 0.01ms !important; }
        }
      `}</style>

      <Nav />

      {/* ── JD Prep Banner ────────────────────────────────────────────────── */}
      <div className="border-b px-6 md:px-10 py-3.5" style={{ background: '#F0F6FF', borderColor: '#E2E8F0' }}>
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: '#EDE9FE' }}>
              <IconSparkle className="w-4 h-4" style={{ color: '#7C3AED' }} />
            </div>
            <div>
              <span className="font-bold" style={{ fontSize: 13, color: '#0F172A' }}>JD Interview Prep — </span>
              <span style={{ fontSize: 13, color: '#64748B' }}>Paste any job description and get 125 tailored questions across 5 chapters.</span>
              <span className="ml-1.5 font-bold" style={{ fontSize: 13, color: '#059669' }}>1 free session.</span>
            </div>
          </div>
          <Link to="/interview-prep"
            className="text-white no-underline px-4 py-1.5 rounded-xl font-bold transition-all duration-200 whitespace-nowrap cursor-pointer"
            style={{ fontSize: 12, background: '#C1272D' }}
            onMouseEnter={e => e.currentTarget.style.background = '#A8202B'}
            onMouseLeave={e => e.currentTarget.style.background = '#C1272D'}>
            Try JD Prep →
          </Link>
        </div>
      </div>

      {/* ── Hero Header with Search ────────────────────────────────────────── */}
      <div className="px-6 md:px-10 pt-12 pb-10" style={{ background: '#FFFFFF', borderBottom: '1px solid #E2E8F0' }}>
        <div className="max-w-7xl mx-auto">
          <p className="mb-2" style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#2563EB' }}>
            {loading ? '' : `${roles.length} roles available`}
          </p>
          <h1 className="font-extrabold tracking-[-0.025em] mb-2" style={{ fontSize: 'clamp(28px, 4vw, 44px)', lineHeight: 1.1, color: '#0F172A' }}>
            Find your role. Ace your interview.
          </h1>
          <p className="mb-4" style={{ fontSize: 15, color: '#64748B' }}>
            1,000 role-specific questions · 100 free (Chapter 1) · AI feedback on every answer
          </p>

          {/* Practice format chips */}
          <div className="flex flex-wrap gap-2 mb-7">
            {[
              { label: 'MCQ',     desc: 'Knowledge recall',  bg: '#EFF6FF', color: '#2563EB' },
              { label: 'Written', desc: 'AI-scored answers', bg: '#EDE9FE', color: '#7C3AED' },
              { label: 'Spoken',  desc: 'Voice training',    bg: '#FEF3C7', color: '#D97706' },
            ].map(({ label, desc, bg, color }) => (
              <div key={label} className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl" style={{ background: bg }}>
                <span className="font-bold" style={{ fontSize: 12, color }}>{label}</span>
                <span style={{ fontSize: 11, color: '#CBD5E1' }}>·</span>
                <span style={{ fontSize: 11, color, opacity: 0.75 }}>{desc}</span>
              </div>
            ))}
            <Link to="/features" className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl no-underline font-semibold transition-colors"
              style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', fontSize: 12, color: '#64748B' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#1B3A5C'; e.currentTarget.style.color = '#1B3A5C'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#E2E8F0'; e.currentTarget.style.color = '#64748B'; }}>
              See all features →
            </Link>
          </div>

          {/* Search bar */}
          <div className="relative max-w-[560px]">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: '#94A3B8' }}>
              <IconSearch className="w-4 h-4" />
            </div>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search roles or categories…"
              className="w-full pl-11 pr-10 py-3.5 rounded-xl text-[15px] outline-none transition-all duration-200 font-medium"
              style={{
                background: '#F8FAFC',
                border: `1.5px solid ${search ? '#1B3A5C' : '#E2E8F0'}`,
                color: '#0F172A',
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
              }}
              onFocus={e => { e.target.style.background = '#FFFFFF'; e.target.style.borderColor = '#1B3A5C'; e.target.style.boxShadow = '0 0 0 3px rgba(27,58,92,0.08)'; }}
              onBlur={e => { e.target.style.background = '#F8FAFC'; e.target.style.borderColor = search ? '#1B3A5C' : '#E2E8F0'; e.target.style.boxShadow = 'none'; }}
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 transition-colors cursor-pointer bg-transparent border-0"
                style={{ color: '#94A3B8' }}
                onMouseEnter={e => e.currentTarget.style.color = '#64748B'}
                onMouseLeave={e => e.currentTarget.style.color = '#94A3B8'}
              >
                <IconX className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Category filter pills */}
          {!loading && categories.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-5">
              {categories.map(({ category, role_count }) => (
                <button
                  key={category}
                  onClick={() => setActiveCategory(category)}
                  className="flex items-center gap-1.5 px-4 py-1.5 rounded-full border font-semibold transition-all duration-150 cursor-pointer"
                  style={{
                    fontSize: 13,
                    background: activeCategory === category ? '#1B3A5C' : '#FFFFFF',
                    borderColor: activeCategory === category ? '#1B3A5C' : '#E2E8F0',
                    color: activeCategory === category ? '#fff' : '#334155',
                    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                  }}
                  onMouseEnter={e => { if (activeCategory !== category) { e.currentTarget.style.borderColor = '#1B3A5C'; e.currentTarget.style.color = '#1B3A5C'; } }}
                  onMouseLeave={e => { if (activeCategory !== category) { e.currentTarget.style.borderColor = '#E2E8F0'; e.currentTarget.style.color = '#334155'; } }}
                >
                  {category}
                  {category !== 'All' && (
                    <span className="text-[11px] font-bold px-1.5 py-0.5 rounded-full"
                      style={{ background: activeCategory === category ? 'rgba(255,255,255,0.25)' : '#F0F6FF', color: activeCategory === category ? '#fff' : '#64748B' }}>
                      {role_count}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Body ──────────────────────────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-8">

        {/* Result count / active filter */}
        {!loading && (
          <div className="flex items-center gap-3 mb-6 flex-wrap">
            <p style={{ fontSize: 14, color: '#64748B' }}>
              {filtered.length === roles.length
                ? `All ${roles.length} roles`
                : `${filtered.length} role${filtered.length !== 1 ? 's' : ''} found`}
              {activeCategory !== 'All' && <span className="font-semibold" style={{ color: '#0F172A' }}> in {activeCategory}</span>}
              {search && <span style={{ color: '#0F172A' }}> for "<strong>{search}</strong>"</span>}
            </p>
            {(activeCategory !== 'All' || search) && (
              <button
                onClick={() => { setSearch(''); setActiveCategory('All'); }}
                className="text-[13px] font-semibold px-3 py-1 rounded-full cursor-pointer border-0 transition-colors duration-150"
                style={{ background: '#FEF2F2', color: '#C1272D', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" }}
                onMouseEnter={e => e.currentTarget.style.background = '#FECACA'}
                onMouseLeave={e => e.currentTarget.style.background = '#FEF2F2'}>
                Clear filters ×
              </button>
            )}
          </div>
        )}

        {loadError ? (
          <div className="text-center py-20 bg-white border rounded-[20px]" style={{ borderColor: '#E2E8F0' }}>
            <p className="font-bold mb-2" style={{ fontSize: 18, color: '#0F172A' }}>Failed to load roles</p>
            <p className="mb-6" style={{ fontSize: 14, color: '#64748B' }}>Check your connection and try again</p>
            <button onClick={loadRoles} className="font-semibold cursor-pointer border-0 px-5 py-2.5 rounded-xl text-white" style={{ background: '#1B3A5C', fontFamily: "'Inter', -apple-system, sans-serif", fontSize: 14 }}>
              Retry
            </button>
          </div>
        ) : loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {Array.from({ length: 12 }).map((_, i) => <SkeletonCard key={i} />)}
          </div>
        ) : filtered.length === 0 ? (
          /* Empty state */
          <div className="text-center py-20 bg-white border rounded-[20px]" style={{ borderColor: '#E2E8F0' }}>
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5" style={{ background: '#F0F6FF' }}>
              <IconSearch className="w-7 h-7" style={{ color: '#94A3B8' }} />
            </div>
            <p className="font-bold mb-2" style={{ fontSize: 18, color: '#0F172A' }}>No roles found</p>
            <p className="mb-6" style={{ fontSize: 14, color: '#64748B' }}>
              {search ? `We couldn't find any roles matching "${search}"` : `No roles in the "${activeCategory}" category yet`}
            </p>
            {(search || activeCategory !== 'All') && (
              <button onClick={() => { setSearch(''); setActiveCategory('All'); }}
                className="font-semibold cursor-pointer bg-transparent border-0 transition-colors px-5 py-2.5 rounded-xl"
                style={{ fontSize: 14, background: '#F0F6FF', color: '#1B3A5C', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" }}
                onMouseEnter={e => { e.currentTarget.style.background = '#1B3A5C'; e.currentTarget.style.color = '#fff'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#F0F6FF'; e.currentTarget.style.color = '#1B3A5C'; }}>
                Show all roles
              </button>
            )}
          </div>
        ) : activeCategory === 'All' ? (
          // Grouped by category
          Object.entries(grouped).map(([cat, catRoles]) => (
            <div key={cat} className="mb-12">
              {/* Sticky category header on mobile */}
              <div className="flex items-center gap-2.5 mb-5 sticky top-16 z-10 py-2 -mx-6 px-6 md:mx-0 md:px-0 md:static"
                style={{ background: '#FFFFFF' }}>
                <h2 className="font-bold" style={{ fontSize: 15, color: '#0F172A' }}>{cat}</h2>
                <span className="font-semibold px-2 py-0.5 rounded-lg" style={{ fontSize: 12, color: '#64748B', background: '#F0F6FF' }}>
                  {catRoles.length}
                </span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {catRoles.map(role => <RoleCard key={role.id} role={role} />)}
              </div>
            </div>
          ))
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filtered.map(role => <RoleCard key={role.id} role={role} />)}
          </div>
        )}

        {/* ── JD Prep section (authenticated users with sessions) ───────────── */}
        {isAuthenticated && jdSessions.length > 0 && (
          <div className="mt-16 mb-4">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="font-bold" style={{ fontSize: 18, color: '#0F172A', margin: 0 }}>Your JD Prep sessions</h2>
                <p style={{ fontSize: 13, color: '#64748B', margin: '4px 0 0' }}>AI-tailored questions from job descriptions you uploaded</p>
              </div>
              <Link to="/jd-prep"
                className="no-underline font-semibold"
                style={{ fontSize: 13, color: '#1B3A5C', transition: 'color 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.color='#C1272D'}
                onMouseLeave={e => e.currentTarget.style.color='#1B3A5C'}>
                View all →
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {jdSessions.map(s => (
                <Link key={s.id} to={`/jd-prep/${s.id}`}
                  className="no-underline block"
                  style={{ borderRadius: 16, background: '#fff', border: '1px solid #E2E8F0', padding: '20px', transition: 'all 0.18s', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor='#1B3A5C'; e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow='0 4px 16px rgba(0,0,0,0.10)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor='#E2E8F0'; e.currentTarget.style.transform='none'; e.currentTarget.style.boxShadow='0 1px 3px rgba(0,0,0,0.06)'; }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: '#EDE9FE', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <IconSparkle className="w-4 h-4" style={{ color: '#7C3AED' }} />
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#0F172A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {s.job_title || 'Untitled role'}
                      </p>
                      {s.company_name && (
                        <p style={{ margin: '2px 0 0', fontSize: 12, color: '#64748B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.company_name}</p>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <span style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6, background: '#F0F6FF', color: '#64748B', fontWeight: 600 }}>
                        {(s.chapter_count || 5) * 25} questions
                      </span>
                      <span style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6, background: '#F0F6FF', color: '#64748B', fontWeight: 600 }}>
                        {new Date(s.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                      </span>
                    </div>
                    <span style={{ fontSize: 12, color: '#1B3A5C', fontWeight: 600 }}>Open →</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
